import type XLSX from 'xlsx-js-style';

import type {
  XlsxAutoFilter,
  XlsxAutoFilterColumn,
  XlsxAutoFilterCustom,
} from '../model/types';
import type { CfBlock } from '../model/conditionalFormatModel';

export type XlsxNativeWorkbook = XLSX.WorkBook;
export type XlsxCellKey = `${number}:${number}`;
export type XlsxParsedScalar = string | number | boolean | Date | null;

export interface XlsxParseLimits {
  maxArchiveEntries: number;
  maxUncompressedBytes: number;
  maxSheets: number;
  maxRows: number;
  maxColumns: number;
  maxMaterializedCells: number;
  maxXmlEntryBytes: number;
  maxSharedStringsBytes: number;
  maxStylesBytes: number;
}

export interface XlsxParseOptions {
  signal?: AbortSignal;
  limits?: Partial<XlsxParseLimits>;
}

export type XlsxParseLimitCode =
  | 'archive_entries'
  | 'archive_uncompressed_bytes'
  | 'sheets'
  | 'rows'
  | 'columns'
  | 'materialized_cells'
  | 'xml_entry_bytes'
  | 'shared_strings_bytes'
  | 'styles_bytes'
  | 'xml_declaration';

export class XlsxParseLimitError extends Error {
  readonly name = 'XlsxParseLimitError';

  constructor(
    readonly code: XlsxParseLimitCode,
    message: string,
  ) {
    super(message);
  }
}

export interface XlsxParsedCell {
  row: number;
  col: number;
  address: string;
  value: XlsxParsedScalar;
  displayText: string;
  rawNumber?: number;
  formula?: string;
  numberFormat?: string;
  styleIndex?: number;
}

export interface XlsxParsedBorderSide {
  style: string;
  color?: string;
}

export interface XlsxParsedBorders {
  top?: XlsxParsedBorderSide;
  bottom?: XlsxParsedBorderSide;
  left?: XlsxParsedBorderSide;
  right?: XlsxParsedBorderSide;
}

/** Semantic style produced by the parser; no CSS or DOM types cross the boundary. */
export interface XlsxParsedCellStyle {
  backgroundColor?: string;
  color?: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  strike?: boolean;
  fontSize?: number;
  fontFamily?: string;
  align?: 'left' | 'center' | 'right';
  verticalAlign?: 'top' | 'middle' | 'bottom';
  wrapText?: boolean;
  textRotation?: number;
  indent?: number;
  shrinkToFit?: boolean;
  borders?: XlsxParsedBorders;
  numberFormat?: string;
}

export interface XlsxParsedColumnStyle {
  min: number;
  max: number;
  style: XlsxParsedCellStyle;
}

export interface XlsxParsedMerge {
  range: string;
  start: { row: number; col: number };
  end: { row: number; col: number };
}

export interface XlsxParsedColumnDimension {
  index: number;
  width?: number;
  wpx?: number;
  wch?: number;
  hidden?: boolean;
}

export interface XlsxParsedRowDimension {
  index: number;
  hpt?: number;
  hpx?: number;
  hidden?: boolean;
}

export interface XlsxParsedRangeRect {
  minR: number;
  maxR: number;
  minC: number;
  maxC: number;
}

export interface XlsxParsedValidation {
  type: string;
  ranges: XlsxParsedRangeRect[];
  staticValues?: string[];
  formula1?: string;
  formula2?: string;
  operator?: string;
  allowBlank?: boolean;
  prompt?: string;
  promptTitle?: string;
  error?: string;
  errorTitle?: string;
}

export interface XlsxParsedSheetFeatures {
  validations: XlsxParsedValidation[];
  autoFilter: XlsxAutoFilter | null;
  freeze: { rows: number; cols: number };
  rowOutlineLevels: Record<string, number>;
}

export interface XlsxParsedHyperlink {
  target: string;
  tooltip?: string;
}

export interface XlsxParsedNote {
  text: string;
  author?: string;
}

export interface XlsxParsedImage {
  mimeType: string;
  base64: string;
  sourcePath: string;
  fromRow: number;
  fromCol: number;
  /** Offset from the `from` cell's top-left corner, in CSS pixels. */
  fromRowOffset: number;
  fromColOffset: number;
  toRow?: number;
  toCol?: number;
  /** Offset from the `to` cell's top-left corner, in CSS pixels. */
  toRowOffset?: number;
  toColOffset?: number;
  width?: number;
  height?: number;
}

/**
 * A `graphicFrame` anchor pointing at a chart part. The part XML is handed to the
 * host verbatim: `c:chartSpace` is shared DrawingML, so hosts already able to draw
 * PPTX charts can reuse that path instead of this package growing a chart model.
 */
export interface XlsxParsedChart {
  /** Contents of the referenced `xl/charts/chartN.xml`. */
  chartXml: string;
  sourcePath: string;
  fromRow: number;
  fromCol: number;
  /** Offset from the `from` cell's top-left corner, in CSS pixels. */
  fromRowOffset: number;
  fromColOffset: number;
  toRow?: number;
  toCol?: number;
  /** Offset from the `to` cell's top-left corner, in CSS pixels. */
  toRowOffset?: number;
  toColOffset?: number;
  width?: number;
  height?: number;
}

export interface XlsxParsedSheet {
  index: number;
  name: string;
  sourcePath: string | null;
  sourceRange: string | null;
  rowCount: number;
  colCount: number;
  cells: Map<XlsxCellKey, XlsxParsedCell>;
  styles: Map<XlsxCellKey, XlsxParsedCellStyle>;
  rowStyles: Map<number, XlsxParsedCellStyle>;
  columnStyles: XlsxParsedColumnStyle[];
  merges: XlsxParsedMerge[];
  columns: XlsxParsedColumnDimension[];
  rows: XlsxParsedRowDimension[];
  features: XlsxParsedSheetFeatures;
  conditionalFormats: CfBlock[];
  hyperlinks: Map<XlsxCellKey, XlsxParsedHyperlink>;
  notes: Map<XlsxCellKey, XlsxParsedNote>;
  images: XlsxParsedImage[];
  charts: XlsxParsedChart[];
  /** `xl/theme/theme1.xml`, read only when the sheet has charts to colour. */
  themeXml?: string;
}

export interface XlsxParseSession {
  /** Editable native workbook clone; lazy parser source remains isolated from mutations. */
  readonly workbook: XlsxNativeWorkbook;
  readonly sheetNames: readonly string[];
  readonly activeSheetIndex: number;
  parseSheet(index: number): Promise<XlsxParsedSheet>;
}

export type {
  CfBlock,
  XlsxAutoFilter,
  XlsxAutoFilterColumn,
  XlsxAutoFilterCustom,
};
